/**
 * DNA Growth Dashboard - Dashboard Executivo do Growth Engine
 * Métricas de Marketing, Captação e Conversão
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  TrendingUp, TrendingDown, Users, Target, DollarSign, BarChart3,
  ArrowUpRight, ArrowDownRight, RefreshCw, Calendar, CheckCircle,
  XCircle, Clock, Eye, MousePointer, Megaphone, PieChart,
  Activity, Zap, Award, Filter
} from 'lucide-react';

export default function DnaGrowthDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [comparativo, setComparativo] = useState(null);
  const [previsao, setPrevisao] = useState(null);
  const [origens, setOrigens] = useState([]);
  const [periodo, setPeriodo] = useState('mes');
  const [loading, setLoading] = useState(true);

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      const [dashRes, compRes, prevRes, origRes] = await Promise.all([
        api.get(`/growth/dashboard?periodo=${periodo}`),
        api.get('/growth/dashboard/comparativo'),
        api.get('/growth/dashboard/previsao'),
        api.get('/growth/dashboard/origens')
      ]);
      
      setDashboard(dashRes.data);
      setComparativo(compRes.data);
      setPrevisao(prevRes.data);
      setOrigens(origRes.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const MetricCard = ({ title, value, icon: Icon, color, trend, trendValue, subtitle }) => (
    <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-500 mb-1">{title}</p>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-xl ${color.replace('text-', 'bg-').replace('600', '100')}`}>
            <Icon className={`w-6 h-6 ${color}`} />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 mt-3 text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            <span>{Math.abs(trend)}% vs mês anterior</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const FunnelBar = ({ etapa, count, total, color }) => {
    const percentage = total > 0 ? (count / total) * 100 : 0;
    
    return (
      <div className="space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">{etapa}</span>
          <span className="font-medium">{count}</span>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className={`h-full ${color} transition-all duration-500`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const metricas = dashboard?.metricas || {};
  const funil = dashboard?.funil || [];
  const totalFunil = funil.reduce((acc, f) => acc + f.count, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Zap className="w-7 h-7 text-amber-500" />
            DNA Growth Engine
          </h1>
          <p className="text-gray-500 text-sm">Dashboard de Captação e Conversão</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={periodo} onValueChange={setPeriodo}>
            <SelectTrigger className="w-36 bg-white">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dia">Hoje</SelectItem>
              <SelectItem value="semana">Esta Semana</SelectItem>
              <SelectItem value="mes">Este Mês</SelectItem>
              <SelectItem value="trimestre">Trimestre</SelectItem>
              <SelectItem value="ano">Este Ano</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="icon" onClick={carregarDados}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Métricas Principais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricCard 
          title="Total de Leads"
          value={metricas.total_leads || 0}
          icon={Users}
          color="text-blue-600"
          trend={comparativo?.variacao?.leads}
          subtitle={`${metricas.novos_leads || 0} novos no período`}
        />
        <MetricCard 
          title="Matrículas"
          value={metricas.matriculas || 0}
          icon={CheckCircle}
          color="text-green-600"
          trend={comparativo?.variacao?.matriculas}
          subtitle={`${metricas.taxa_conversao || 0}% de conversão`}
        />
        <MetricCard 
          title="Em Andamento"
          value={metricas.em_andamento || 0}
          icon={Clock}
          color="text-amber-600"
          subtitle={`${metricas.propostas_ativas || 0} propostas ativas`}
        />
        <MetricCard 
          title="Valor Pipeline"
          value={`R$ ${(metricas.valor_pipeline || 0).toLocaleString('pt-BR')}`}
          icon={DollarSign}
          color="text-purple-600"
          subtitle={`R$ ${(metricas.valor_matriculas || 0).toLocaleString('pt-BR')} fechados`}
        />
      </div>

      {/* Grid Principal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Funil de Conversão */}
        <Card className="lg:col-span-2 bg-white border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-600" />
              Funil de Conversão
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {funil.map((etapa, idx) => {
                const colors = [
                  'bg-blue-500', 'bg-indigo-500', 'bg-purple-500',
                  'bg-amber-500', 'bg-orange-500', 'bg-pink-500', 'bg-green-500'
                ];
                return (
                  <FunnelBar 
                    key={etapa.status}
                    etapa={etapa.etapa}
                    count={etapa.count}
                    total={totalFunil}
                    color={colors[idx % colors.length]}
                  />
                );
              })}
            </div>
            
            <div className="mt-6 pt-4 border-t flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-500">Taxa de Conversão Geral</p>
                <p className="text-2xl font-bold text-green-600">{metricas.taxa_conversao || 0}%</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Perdidos</p>
                <p className="text-xl font-bold text-red-500">{metricas.perdidos || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Previsão */}
        <Card className="bg-gradient-to-br from-blue-600 to-purple-600 text-white border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-white">
              <TrendingUp className="w-5 h-5" />
              Previsão de Matrículas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {previsao && (
              <div className="space-y-4">
                <div className="text-center py-4">
                  <p className="text-4xl font-bold">{previsao.total_matriculas_previstas}</p>
                  <p className="text-blue-200">matrículas previstas</p>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-200">Cenário Pessimista</span>
                    <span>{previsao.cenarios?.pessimista?.matriculas}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium">
                    <span>Cenário Realista</span>
                    <span>{previsao.cenarios?.realista?.matriculas}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-200">Cenário Otimista</span>
                    <span>{previsao.cenarios?.otimista?.matriculas}</span>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-white/20">
                  <p className="text-sm text-blue-200">Receita Prevista</p>
                  <p className="text-xl font-bold">
                    R$ {(previsao.receita_prevista || 0).toLocaleString('pt-BR')}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Origens e ROI */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Performance por Origem */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="w-5 h-5 text-purple-600" />
              Performance por Origem
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {origens.slice(0, 6).map((origem, idx) => {
                const icons = ['📸', '👤', '💬', '🌐', '👥', '📢', '🎉', '📞', '🏫', '📋'];
                return (
                  <div key={origem.origem} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="text-2xl">{icons[idx % icons.length]}</span>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-gray-800 capitalize">{origem.origem}</span>
                        <span className="text-sm text-gray-500">{origem.total_leads} leads</span>
                      </div>
                      <Progress value={origem.taxa_conversao} className="h-2" />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>{origem.matriculas} matrículas</span>
                        <span className="font-medium text-green-600">{origem.taxa_conversao}% conversão</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* ROI e Atividade */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-green-600" />
              ROI e Atividade
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 bg-green-50 rounded-xl text-center">
                <Award className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-700">
                  {dashboard?.roi?.roi_percentual || 0}%
                </p>
                <p className="text-sm text-green-600">ROI</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-xl text-center">
                <DollarSign className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-700">
                  R$ {((dashboard?.roi?.receita || 0) / 1000).toFixed(0)}k
                </p>
                <p className="text-sm text-blue-600">Receita</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Interações no Período</span>
                <span className="font-bold text-blue-600">{dashboard?.atividade?.interacoes_periodo || 0}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Tarefas Pendentes</span>
                <span className="font-bold text-amber-600">{dashboard?.atividade?.tarefas_pendentes || 0}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Tarefas Vencidas</span>
                <span className="font-bold text-red-600">{dashboard?.atividade?.tarefas_vencidas || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leads Recentes */}
      <Card className="bg-white border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Leads Recentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {dashboard?.leads_recentes?.map((lead, idx) => (
              <div key={lead.id || idx} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-blue-600 font-bold">{lead.nome?.charAt(0)?.toUpperCase()}</span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-800">{lead.nome}</p>
                  <p className="text-sm text-gray-500">{lead.telefone || lead.email}</p>
                </div>
                <Badge variant="outline" className="capitalize">{lead.origem}</Badge>
                <span className="text-xs text-gray-400">
                  {new Date(lead.created_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                </span>
              </div>
            ))}
            
            {(!dashboard?.leads_recentes || dashboard.leads_recentes.length === 0) && (
              <p className="text-center text-gray-400 py-8">
                Nenhum lead recente
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
